import { useCallback, useEffect, useMemo, useState } from 'react';
import { chargeMapService } from '../services/chargeMapService';
import { loadChargeMap, reportWithMaps, retailerOf } from '../services/billReportLoader';
import type { BillReport, BillVerdict } from '../lib/billVerdict';
import type { ChargeMapRow } from '../utils/chargeMap';
import type { NameAliases } from '../utils/nameAliases';
import type { BvBill } from '../types/billTypes';

/**
 * `billReport` for many bills, with each retailer's charge map loaded once.
 * Until a retailer's map arrives its bills are judged without it, the way the
 * Comparison table pairs on name alone while its own map loads. Outside React
 * (a one-off export) use `loadBillReporter`, which waits for the maps.
 */
export const useBillReports = (bills: Partial<BvBill>[], tolerancePct?: number) => {
  const [maps, setMaps] = useState<Record<string, ChargeMapRow[]>>({});
  const [aliases, setAliases] = useState<NameAliases | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    void chargeMapService.distributorAliases().then((a) => { if (mounted) setAliases(a); });
    return () => { mounted = false; };
  }, []);

  const names = useMemo(
    () => [...new Set(bills.map(retailerOf).filter((n): n is string => !!n))].sort(),
    [bills],
  );

  useEffect(() => {
    let mounted = true;
    void Promise.all(names.map(async (n) => [n, await loadChargeMap(n)] as const)).then((pairs) => {
      if (mounted) setMaps(Object.fromEntries(pairs));
    });
    return () => { mounted = false; };
  }, [names]);

  return useCallback(
    (bill: Partial<BvBill>): BillReport | null => reportWithMaps(bill, maps, aliases, tolerancePct),
    [maps, aliases, tolerancePct],
  );
};

/** `useBillReports`, verdict only. */
export const useBillVerdicts = (bills: Partial<BvBill>[]) => {
  const report = useBillReports(bills);
  return useCallback((bill: Partial<BvBill>): BillVerdict | null => report(bill)?.verdict ?? null, [report]);
};
